import type React from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { installVisualDebugConsoleToggle } from '@/lib/debug/visual-debug-flag'
import { useVisualDebug } from '@/hooks/use-visual-debug'
import { DebugBoundingBoxes } from '@/components/app/editor/visual-layer/components/visual-debug-layer'
import {
  SelectionChromeBadgeOverlay,
  SelectionChromeLayer,
} from '@/components/app/editor/visual-layer/selection-chrome-layer'
import { TextOverflowBadgeLayer } from '@/components/app/editor/visual-layer/components/text-overflow-badge-layer'
import { HoverOutlineLayer } from '@/components/app/editor/visual-layer/components/hover-outline-layer'
import {
  DragRectLayer,
  type DragRectVariant,
} from '@/components/app/editor/visual-layer/components/drag-rect-layer'
import type { Rect } from '@/lib/shapes/shape-coordinates'
import {
  SmartGuidesLayer,
  SmartGuidesRotationLabel,
} from '@/components/app/editor/visual-layer/components/smart-guides-layer'
import type { HorizontalGuideSpan } from '@/components/app/document/overlays/guides-overlay'
import type { SmartGuideLine } from '@/lib/guides/smart-guides'

import { usePolledCanvas } from './hooks/use-polled-canvas'

// Installed at module load, not from an effect: the console toggle must exist even if nothing here
// ever mounts, or it reports `__larryVisualDebug is not defined` with no clue why.
installVisualDebugConsoleToggle()

/**
 * The visual layer: everything drawn *about* the document rather than *in* it.
 *
 * Mounted as a sibling of the canvas `scale()` wrapper, so it receives `scale` and computes final
 * screen pixels itself. That is what lets chrome use literal sizes — a 1px stroke is 1px, an 8px
 * handle is 8px — instead of counter-scaling against the transform and rounding differently at every
 * zoom level.
 *
 * Currently owns selection chrome (frame, resize anchors, rotation handle), the alt-hover cycling
 * outline, transient drag rects (marquee, zoom marquee, text create preview), smart guides, the
 * text-overflow badge, and the geometry debug overlay. Node-edit handles and remote-user chrome
 * are natural next tenants — see the layer notes in
 * `selection-chrome-layer/selection-chrome-layer.tsx`.
 */

export type VisualLayerProps = {
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  /** Current canvas zoom. Applied here explicitly rather than inherited from a CSS transform. */
  scale: number
  /** Suppress chrome while another surface owns the selection (node edit, text tool, mixed selection). */
  suppressChrome?: boolean
  /**
   * Transient drag rectangles in document space (marquee, zoom marquee, create preview).
   *
   * Passed as props rather than polled: these live in `center-canvas` state and the drag producing
   * them already re-renders it every frame.
   */
  dragRects?: Array<{ variant: DragRectVariant; rect: Rect | null }>
  /**
   * Active smart guides (alignment lines + rotation readout), in document space.
   *
   * Passed as a prop, not polled, for the same reason as `dragRects`: this is already
   * `center-canvas` React state, fed by whichever manager is running the current drag, and that
   * drag already re-renders `center-canvas` every frame.
   */
  smartGuides?: SmartGuideLine[]
  /** Clips horizontal smart-guide segments to a single page span. */
  smartGuidesHorizontalClipSpan?: HorizontalGuideSpan | null
}

export function VisualLayer({
  shapeManagerRef,
  scale,
  suppressChrome,
  dragRects,
  smartGuides,
  smartGuidesHorizontalClipSpan,
}: VisualLayerProps) {
  const { enabled: debugEnabled } = useVisualDebug()
  // Hover chrome stays useful while another surface owns the selection (node edit, the text tool, a
  // mixed shape+text selection), so polling and hover are unconditional — only the selection chrome
  // itself respects `suppressChrome`.
  const chromeEnabled = !suppressChrome
  const {
    shapes,
    selected,
    multiSelectChrome,
    selectionState,
    altHoveredShape,
    overflowBadgeShapes,
    chainPeerShapes,
    chainPeerStateById,
  } = usePolledCanvas(shapeManagerRef)

  return (
    <>
      <svg
        className="absolute inset-0"
        style={{ pointerEvents: 'none', overflow: 'visible' }}
        width="100%"
        height="100%"
        data-visual-layer-canvas=""
      >
        {debugEnabled && <DebugBoundingBoxes shapes={shapes} scale={scale} />}
        <HoverOutlineLayer shape={altHoveredShape} scale={scale} />
        {/* Chain peers, before the real chrome so the true selection paints on top where boxes
            overlap. One layer per peer with a single-shape `selected`, which is what makes each box
            read as an ordinary single selection — solid frame, handles, rotate stem — rather than a
            multi-select member outline. `interactive={false}` keeps every one of them inert.
            No `shapeManagerRef`: there is nothing to call when nothing is hit-testable. */}
        {chromeEnabled &&
          chainPeerShapes.map((peer) => (
            <SelectionChromeLayer
              key={`chain-peer-${peer.id}`}
              selected={[peer]}
              multiSelectChrome={null}
              selectionState={{
                // No badge: a peer is not the selection, so a lock/freeze badge there would offer an
                // unlock button for something the user has not selected.
                badge: null,
                locked: chainPeerStateById.get(peer.id)?.locked ?? false,
                frozenOnly: chainPeerStateById.get(peer.id)?.frozenOnly ?? false,
                geometryBlockedPulseKey: 0,
              }}
              scale={scale}
              interactive={false}
            />
          ))}
        {chromeEnabled && (
          <SelectionChromeLayer
            shapeManagerRef={shapeManagerRef}
            selected={selected}
            multiSelectChrome={multiSelectChrome}
            selectionState={selectionState}
            scale={scale}
          />
        )}
        {/* Last, so a marquee draws over chrome — matching the z-index it had in-canvas. */}
        {dragRects && <DragRectLayer rects={dragRects} scale={scale} />}
        {smartGuides && (
          <SmartGuidesLayer
            guides={smartGuides}
            scale={scale}
            horizontalClipSpan={smartGuidesHorizontalClipSpan}
          />
        )}
      </svg>
      {/* HTML sibling, not inside the <svg>: the badge is a real button with a tooltip and needs a
          normal layout box to position against. */}
      {chromeEnabled && (
        <SelectionChromeBadgeOverlay
          shapeManagerRef={shapeManagerRef}
          selected={selected}
          multiSelectChrome={multiSelectChrome}
          selectionState={selectionState}
          scale={scale}
        />
      )}
      {/* Unconditional, unlike the lock/freeze badge above: the overflow badge isn't "selection
          chrome" in the sense `suppressChrome` means (node edit, crop, the text tool owning the
          surface) — `ShapeTextEditor` rendered it regardless of any of those before it moved here,
          and this preserves that. */}
      <TextOverflowBadgeLayer shapes={overflowBadgeShapes} scale={scale} />
      {smartGuides && <SmartGuidesRotationLabel guides={smartGuides} scale={scale} />}
    </>
  )
}

export default VisualLayer
